/**
 * ASSIST-05 item 6 — “My purchase” in the desktop header and on the account
 * screen, for a signed-in user with at least one open case.
 *
 * Client-only (R2a.5): the cases are read in an effect, so nothing here is in
 * any server render; on the Vike pages HeaderSSR shows it only after
 * hydration. Guests, an empty answer and any failure render nothing, which is
 * also what the sales demo (an unresolvable backend) gets. No mode gate:
 * `get_my_active_case` checks participation only (R2a.2).
 *
 * The header links to the newest open case; the account screen lists each one.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, KeyRound } from 'lucide-react';
import { fetchMyActiveCases } from '../../../lib/api/assistRoomApi';
import { roomCopy } from '../../../src/lib/assist/copy/room';
import type { ActiveCase } from './roomModel';

/** The open cases of `viewerId` (null or undefined: a guest, nothing is read). */
export function useMyActiveCases(viewerId: string | null | undefined): ActiveCase[] {
  const [state, setState] = useState<{ viewer: string | null; cases: ActiveCase[] }>({ viewer: null, cases: [] });
  useEffect(() => {
    if (!viewerId) return;
    let cancelled = false;
    fetchMyActiveCases().then((cases) => {
      if (!cancelled) setState({ viewer: viewerId, cases });
    });
    return () => {
      cancelled = true;
    };
  }, [viewerId]);
  // Cases read for another user are never shown to this one.
  return viewerId && state.viewer === viewerId ? state.cases : [];
}

export const purchasePath = (caseId: string) => `/purchases/${caseId}`;

/**
 * The header entry. `anchor` renders a plain <a> for HeaderSSR, which has no
 * router; the SPA Navbar gets a router Link. The caller passes its own nav
 * item classes so the entry looks like its neighbours.
 */
export function MyPurchaseNavLink({
  viewerId,
  className,
  anchor = false,
}: {
  viewerId: string | null | undefined;
  className: string;
  anchor?: boolean;
}) {
  const cases = useMyActiveCases(viewerId);
  if (cases.length === 0) return null;
  const href = purchasePath(cases[0].caseId);
  const inner = (
    <>
      <KeyRound size={18} aria-hidden="true" />
      <span>{roomCopy.nav.myPurchase}</span>
    </>
  );
  return anchor ? (
    <a href={href} className={className}>
      {inner}
    </a>
  ) : (
    <Link to={href} className={className}>
      {inner}
    </Link>
  );
}

/** The account screen's rows: one per open case, styled like the screen's own links. */
export function MyPurchaseRows({ viewerId }: { viewerId: string | null | undefined }) {
  const cases = useMyActiveCases(viewerId);
  if (cases.length === 0) return null;
  return (
    <div className="bg-white rounded-2xl overflow-hidden shadow-sm">
      {cases.map((c) => (
        <Link
          key={c.caseId}
          to={purchasePath(c.caseId)}
          className="px-4 py-3 flex items-center justify-between border-t border-gray-100 first:border-t-0 hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center gap-3 min-w-0">
            <span className="text-gray-400">
              <KeyRound size={20} aria-hidden="true" />
            </span>
            <div className="min-w-0">
              <span className="text-gray-900 font-medium">{roomCopy.nav.myPurchase}</span>
              <p className="text-gray-500 text-xs truncate">{c.title ?? roomCopy.nav.untitled}</p>
            </div>
          </div>
          <ChevronRight size={18} className="text-gray-300" aria-hidden="true" />
        </Link>
      ))}
    </div>
  );
}
